// components/ui/CurrencyDisplay.tsx
import React from 'react'

interface CurrencyDisplayProps {
  value: number
  currency?: string
  locale?: string
  showSymbol?: boolean
  showCode?: boolean
  showSign?: boolean
  precision?: number
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  weight?: 'normal' | 'medium' | 'semibold' | 'bold'
  color?: string
  className?: string
  positiveColor?: string
  negativeColor?: string
  zeroColor?: string
}

const sizeClasses = {
  xs: 'text-xs',
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
  '2xl': 'text-2xl'
}

const weightClasses = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold'
}

export const CurrencyDisplay: React.FC<CurrencyDisplayProps> = ({
  value,
  currency = 'USD',
  locale = 'en-US',
  showSymbol = true,
  showCode = false,
  showSign = false,
  precision = 2,
  size = 'md',
  weight = 'normal',
  color,
  className = '',
  positiveColor = 'text-white',
  negativeColor = 'text-red-400',
  zeroColor = 'text-gray-400'
}) => {
  const isNegative = value < 0
  const isZero = value === 0
  const absoluteValue = Math.abs(value)

  // Determine color based on value
  let valueColor = color
  if (!valueColor) {
    if (isNegative) {
      valueColor = negativeColor
    } else if (isZero) {
      valueColor = zeroColor
    } else {
      valueColor = positiveColor
    }
  }

  // Format the number
  const formatter = new Intl.NumberFormat(locale, {
    style: showSymbol || showCode ? 'currency' : 'decimal',
    currency: currency,
    currencyDisplay: showCode ? 'code' : 'symbol',
    minimumFractionDigits: precision,
    maximumFractionDigits: precision
  })

  let formattedValue = formatter.format(absoluteValue)

  // Handle sign display
  if (showSign) {
    if (isNegative) {
      formattedValue = `-${formattedValue}`
    } else if (value > 0) {
      formattedValue = `+${formattedValue}`
    }
  } else if (isNegative) {
    formattedValue = `-${formattedValue}`
  }

  // Custom formatting if not using Intl.NumberFormat for currency
  if (!showSymbol && !showCode) {
    formattedValue = absoluteValue.toLocaleString(locale, {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision
    })
    if (showSign) {
      if (isNegative) {
        formattedValue = `-${formattedValue}`
      } else if (value > 0) {
        formattedValue = `+${formattedValue}`
      }
    } else if (isNegative) {
      formattedValue = `-${formattedValue}`
    }

    if (showCode) {
      formattedValue = `${formattedValue} ${currency}`
    }
  }

  return (
    <span
      className={`
        ${sizeClasses[size]}
        ${weightClasses[weight]}
        ${valueColor}
        ${className}
      `}
      title={`${value.toFixed(precision)} ${currency}`}
    >
      {formattedValue}
    </span>
  )
}

// Shorthand components
export const CurrencyPositive: React.FC<Omit<CurrencyDisplayProps, 'showSign'>> = (props) => (
  <CurrencyDisplay showSign={true} positiveColor="text-green-400" {...props} />
)

export const CurrencyNegative: React.FC<Omit<CurrencyDisplayProps, 'showSign'>> = (props) => (
  <CurrencyDisplay showSign={true} negativeColor="text-red-400" {...props} />
)

export const CurrencyChange: React.FC<Omit<CurrencyDisplayProps, 'showSign'>> = ({ value, ...props }) => {
  if (value > 0) {
    return <CurrencyPositive value={value} {...props} />
  } else if (value < 0) {
    return <CurrencyNegative value={value} {...props} />
  } else {
    return <CurrencyDisplay value={value} zeroColor="text-gray-400" {...props} />
  }
}

// Helper function for formatting
export const formatCurrency = (
  value: number,
  options?: {
    currency?: string
    locale?: string
    precision?: number
    showSymbol?: boolean
  }
): string => {
  const {
    currency = 'USD',
    locale = 'en-US',
    precision = 2,
    showSymbol = true
  } = options || {}

  return new Intl.NumberFormat(locale, {
    style: showSymbol ? 'currency' : 'decimal',
    currency: currency,
    minimumFractionDigits: precision,
    maximumFractionDigits: precision
  }).format(value)
}